import { AxiosError, HttpStatusCode } from "axios";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";
import ApiService from "@/src/application/services/api-service";
import AuthService, { AuthenticationStatus } from "@/src/application/services/auth-service";
import type ErrorResponse from "@/src/infrastructure/dtos/error-response";
import RegisterError, { getRegisterErrorMessage } from "@/src/infrastructure/errors/register-error";
import { AuthApi } from "@generated/n3-backend-api";
import BusinessErrorEnum from "@/src/domain/enums/business-error-enum";
import { BusinessException } from "@/src/domain/common/business-exception";
import { getCurrentBrandConfig } from "@/src/config/brand";

@injectable()
export default class AuthServiceImpl extends AuthService {
  @observable
  status = AuthenticationStatus.Idle;

  @action
  setStatus(status: AuthenticationStatus): void {
    this.status = status;
  }

  constructor(
    @inject(ApiService) private readonly apiService: ApiService,
    @inject(AuthApi) private readonly authApi: AuthApi
  ) {
    super();
    this.apiService = apiService;
    makeObservable(this);
  }

  async login(email: string, password: string, rememberMe?: boolean) {
    try {
      const response = await this.authApi.authenticateUser({
        email,
        password,
        rememberMe,
        loginMethod: "COOKIE",
      });
      if (response.status === HttpStatusCode.Ok) {
        this.setStatus(AuthenticationStatus.RequestUserInfo);
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        const errorResponse = e.response?.data;
        if (errorResponse?.type === "USER_HAS_NO_DEVICES") {
          throw new BusinessException(errorResponse.message, e, BusinessErrorEnum.UserHasNoDevices);
        }
      }
      throw new BusinessException(BusinessErrorEnum.Unauthorized, e);
    }
  }

  @action
  async logout() {
    await this.authApi.logout();
    runInAction(() => {
      this.status = AuthenticationStatus.NotAuthenticated;
    });
  }

  async register(email: string, password: string, serialNumber: string) {
    try {
      await this.authApi.registerUser({
        email,
        password,
        serialNumber,
        platform: "WEB",
      });
    } catch (e) {
      if (e instanceof AxiosError) {
        const errorResponse = e.response?.data as ErrorResponse;
        if (errorResponse) {
          throw new RegisterError(
            errorResponse.type,
            errorResponse.message ||
              getRegisterErrorMessage(
                errorResponse.type,
                getCurrentBrandConfig().strings.councilName
              )
          );
        }
      }
      throw e;
    }
  }
}
